import React, { useContext, useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useProjectLineClock } from "../hooks";
import { UserDataContext } from "../app/App";
import { Grid, Box, CircularProgress, TextField, Typography, List, ListItem, ListItemText, IconButton, AppBar, Toolbar, Dialog, DialogTitle, DialogActions, DialogContent, Slide, Button, Link, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Fullscreen, Close, Phone } from "@material-ui/icons";
import SamsynMap from "../components/custom/SamsynMap";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import Layout from "../components/Layout/Layout";
import ErrorPage from "./ErrorPage";
import { useSnackbar } from "notistack";
import { ReportIncidentButton, OpenPhotosButton } from "../components";
import { useFetchGardeningManagersQuery, useUpdateWorklineManagerMutation, useUpdateProjectDescriptionMutation, useCreateGardeningWorklineMutation } from "../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DurationInput from "../components/DurationInput/DurationInput";
import { parseDurationHours } from "../utils/duration";

dayjs.extend(utc);

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function GardeningClock() {
    const { id: projectLineId } = useParams();
    const { user } = useContext(UserDataContext);
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    // The admin flag rides along in history state from the gardening admin list. A deep link or a
    // hard refresh loses it and falls back to the plain worker view, which is the safe default.
    // The privilege is re-checked here so history state alone cannot hand out edit rights.
    const isAdminView =
        location.state?.isAdminChecked === true && (user?.crmPrivileges ?? []).some((p) => p.name === "Garðyrkja super");

    const [issue, setIssue] = useState("");
    const [managerId, setManagerId] = useState("");
    const [description, setDescription] = useState("");
    const [workEntryOpen, setWorkEntryOpen] = useState(false);
    const [hours, setHours] = useState("");
    const [workDescription, setWorkDescription] = useState("");
    const [previewEnd, setPreviewEnd] = useState(() => dayjs.utc());
    const parsedHours = parseDurationHours(hours);
    const validHours = parsedHours !== null && parsedHours > 0 && parsedHours <= 24;
    const [totalInGroup, setTotalInGroup] = useState(1);
    const [fullscreen, setFullscreen] = useState(false);
    const [fullscreenLabelAndDescription, setFullscreenLabelAndDescription] = useState({ label: "", description: "" });

    const {
        startWork,
        stopWork,
        skipWork,
        updateIssue,
        isLoading,
        isLoadingStartProjectLine,
        isLoadingStopProjectLine,
        isLoadingSkipProjectLine,
        isSuccess,
        isLoadingUpdateIssue,
        isError,
        projectLine,
        relatedProjectLines,
        projectLineDetails,
    } = useProjectLineClock({ projectLineId, workType: "gardening" });

    const { data: managers } = useFetchGardeningManagersQuery({ enabled: isAdminView });
    const { mutate: createWorkline, isLoading: isCreatingWorkline } = useCreateGardeningWorklineMutation({
        onSuccess: () => {
            setWorkEntryOpen(false);
            setHours("");
            setWorkDescription("");
            enqueueSnackbar("Vinnulína skráð", { variant: "success" });
        },
        onError: () => enqueueSnackbar("Ekki tókst að skrá vinnulínu", { variant: "error" }),
    });

    useEffect(() => {
        if (!workEntryOpen) return;
        const timer = setInterval(() => {
            const end = dayjs.utc();
            setPreviewEnd((previous) => previous.valueOf() === end.valueOf() ? previous : end);
        }, 1000);
        return () => clearInterval(timer);
    }, [workEntryOpen]);
    const { mutate: saveManager, isLoading: isSavingManager } = useUpdateWorklineManagerMutation({
        onSuccess: () => enqueueSnackbar("Manager updated", { variant: "success" }),
        onError: () => enqueueSnackbar("Error updating manager", { variant: "error" }),
    });
    const { mutate: saveDescription, isLoading: isSavingDescription } = useUpdateProjectDescriptionMutation({
        onSuccess: () => enqueueSnackbar("Description updated", { variant: "success" }),
        onError: () => enqueueSnackbar("Error updating description", { variant: "error" }),
    });

    // Last values seen from the server, so a background refetch does not overwrite unsaved edits.
    const syncedFromServer = useRef({ managerId: null, description: null });

    const isStarted = projectLineDetails.hasStartedWork;
    const canEdit = isAdminView && !isStarted;
    const lockedHelperText = isAdminView && isStarted ? "Verkið er hafið - ekki hægt að breyta" : undefined;

    const managerIsDirty = managerId !== (projectLine?.manager?.id ?? "");
    const descriptionIsDirty = description !== (projectLine?.project?.description ?? "");

    useEffect(() => {
        if (projectLine) {
            const serverManagerId = projectLine?.manager?.id ?? "";
            const serverDescription = projectLine?.project?.description ?? "";

            setIssue(projectLine?.issue ?? "");
            setTotalInGroup(projectLine?.totalInGroup ?? 1);

            // refetchOnWindowFocus is on, so only adopt a server value that actually changed -
            // otherwise a refetch mid-edit would throw away what the manager just typed or picked.
            if (serverManagerId !== syncedFromServer.current.managerId) {
                syncedFromServer.current.managerId = serverManagerId;
                setManagerId(serverManagerId);
            }
            if (serverDescription !== syncedFromServer.current.description) {
                syncedFromServer.current.description = serverDescription;
                setDescription(serverDescription);
            }

            setFullscreenLabelAndDescription({
                label: "Description",
                description: serverDescription || "Óskráð",
            });
        }
    }, [projectLine]);

    if (isLoading) {
        return (
            <Layout color="rgb(225 190 16)" title="Gardening" map={false}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }
    if (isError) {
        return <ErrorPage />;
    }

    const handleUpdateLine = (actionType) => {
        if (actionType === "start") {
            startWork(user.id, 1);
        } else if (actionType === "stop") {
            stopWork(user.id, 1);
        } else if (actionType === "skip") {
            skipWork(user.id, 1);
        }
    };

    return (
        <>
            <Layout
                color="rgb(225 190 16)"
                title={
                    projectLine?.project?.address?.name && projectLine?.product?.name
                        ? `${projectLine?.project?.address?.name} - ${projectLine?.product?.name}`
                        : projectLine?.project?.address?.name
                        ? projectLine?.project?.address?.name
                        : projectLine?.project?.name
                        ? projectLine?.project?.name
                        : "Óskráð"
                }
                subtitle={user?.name ?? "Gardening"}
                map={
                    projectLine?.project?.address?.latitude && projectLine?.project?.address?.longitude
                        ? { latitude: projectLine?.project?.address?.latitude, longitude: projectLine?.project?.address?.longitude }
                        : false
                }
            >
                <Grid container spacing={3}>
                    {isAdminView && (
                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" fullWidth onClick={() => {
                                setPreviewEnd(dayjs.utc());
                                setWorkEntryOpen(true);
                            }}>
                                Skrá vinnu
                            </Button>
                        </Grid>
                    )}
                    {/* Manager */}
                    <Grid item xs={12}>
                        {canEdit ? (
                            <Box display="flex" alignItems="flex-start" style={{ gap: "0.5rem" }}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel id="manager-label" shrink>
                                        Verkstjóri
                                    </InputLabel>
                                    <Select
                                        labelId="manager-label"
                                        label="Verkstjóri"
                                        displayEmpty
                                        value={managerId}
                                        disabled={isSavingManager}
                                        onChange={(event) => setManagerId(event.target.value)}
                                    >
                                        <MenuItem value="">Án verkstjóra</MenuItem>
                                        {(managers ?? []).map((manager) => (
                                            <MenuItem key={manager.id} value={manager.id}>
                                                {manager.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={isSavingManager}
                                    disabled={!managerIsDirty || isSavingManager}
                                    onClick={() => saveManager({ projectLineId, managerId: managerId || null })}
                                >
                                    Vista
                                </LoadingButton>
                            </Box>
                        ) : (
                            <TextField
                                label="Verkstjóri"
                                value={projectLine?.manager?.name ?? "Óskráð"}
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: true }}
                                helperText={lockedHelperText}
                            />
                        )}
                    </Grid>

                    {/* Contact. The orange "call before arrival" treatment is an instruction to the
                        worker, so it only appears when the line actually carries that flag. The admin
                        view gets the same contact as a plain row, for reaching them when needed. */}
                    {projectLine?.project?.approvedBy && (projectLine?.callBeforeArrival || isAdminView) && (
                        <Grid item xs={12}>
                            <Box
                                display="flex"
                                alignItems="center"
                                sx={
                                    projectLine?.callBeforeArrival
                                        ? { backgroundColor: "#fff3e0", borderRadius: 1, p: 1.5 }
                                        : { border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }
                                }
                            >
                                <Phone sx={{ mr: 1, color: projectLine?.callBeforeArrival ? "#e65100" : "text.secondary" }} />
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                        color={projectLine?.callBeforeArrival ? "inherit" : "textSecondary"}
                                    >
                                        {projectLine?.callBeforeArrival ? "Call before arrival" : "Contact"}
                                    </Typography>
                                    <Typography variant="body2">
                                        {[projectLine.project.approvedBy.firstName, projectLine.project.approvedBy.lastName].filter(Boolean).join(" ") || "Unknown"}
                                        {projectLine.project.approvedBy.mobilePhone && (
                                            <>
                                                {" — "}
                                                <Link href={`tel:${projectLine.project.approvedBy.mobilePhone}`} underline="always">
                                                    {projectLine.project.approvedBy.mobilePhone}
                                                </Link>
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}

                    {/* Description */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center">
                            <IconButton
                                onClick={() => {
                                    setFullscreen(true);
                                }}
                                aria-label="Full screen"
                                size="medium"
                            >
                                <Fullscreen />
                            </IconButton>
                        </Box>
                        {canEdit ? (
                            <>
                                <TextField
                                    id="outlined-multiline-static"
                                    label="Description"
                                    multiline
                                    minRows={4}
                                    value={description}
                                    disabled={isSavingDescription}
                                    onChange={(event) => setDescription(event.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                                <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        loading={isSavingDescription}
                                        disabled={!descriptionIsDirty || isSavingDescription}
                                        onClick={() => saveDescription({ projectId: projectLine?.project?.id, projectLineId, description })}
                                    >
                                        Vista
                                    </LoadingButton>
                                </Box>
                            </>
                        ) : (
                            <TextField
                                id="outlined-multiline-static"
                                label="Description"
                                multiline
                                minRows={4}
                                value={projectLine?.project?.description ?? "Óskráð"}
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: true }}
                                helperText={lockedHelperText}
                                onClick={() => {
                                    setFullscreen(true);
                                    setFullscreenLabelAndDescription({
                                        label: "Description",
                                        description: projectLine?.project?.description ?? "Óskráð",
                                    });
                                }}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            id="outlined-multiline-static"
                            label="Issue"
                            multiline
                            minRows={4}
                            value={issue}
                            disabled={isLoading}
                            onChange={(event) => setIssue(event.target.value)}
                            onBlur={() => {
                                if (issue !== projectLine?.issue) {
                                    if (issue === "") {
                                        setIssue(projectLine?.issue ?? "");
                                        return;
                                    }
                                    updateIssue({
                                        ProjectLineId: projectLineId,
                                        Issue: issue,
                                    });
                                }
                            }}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            fullWidth
                            loading={isLoadingStartProjectLine}
                            disabled={projectLineDetails.hasStartedWork || projectLineDetails.isWorkOver || isLoadingStartProjectLine}
                            onClick={() => handleUpdateLine("start")}
                        >
                            {projectLineDetails.hasStartedWork ? `Started: ${projectLineDetails.startDateFormatted}` : "Start"}
                        </LoadingButton>
                    </Grid>
                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            fullWidth
                            loading={isLoadingStopProjectLine}
                            disabled={!projectLineDetails.hasStartedWork || projectLineDetails.hasEndedWork || projectLineDetails.isWorkOver || isLoadingStopProjectLine}
                            onClick={() => handleUpdateLine("stop")}
                        >
                            {projectLineDetails.hasEndedWork ? `Stopped: ${projectLineDetails.endDateFormatted}` : "Stop"}
                        </LoadingButton>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            variant="outlined"
                            label="Time estimate / actual"
                            value={`${projectLine?.quantity ?? "-"} / ${projectLineDetails?.totalTime ?? 0}`}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            variant="outlined"
                            label="Fjöldi í hóp"
                            type="number"
                            disabled={isLoading || isLoadingUpdateIssue}
                            value={totalInGroup}
                            onChange={(event) => setTotalInGroup(parseInt(event.target.value))}
                            onBlur={() => {
                                if (totalInGroup !== projectLine?.totalInGroup) {
                                    if (totalInGroup < 1) {
                                        setTotalInGroup(projectLine?.totalInGroup ?? 1);
                                        return;
                                    }
                                    updateIssue({
                                        // Update totalInGroup
                                        ProjectLineId: projectLineId,
                                        Issue: issue,
                                        TotalInGroup: totalInGroup,
                                    });
                                }
                            }}
                            fullWidth
                            InputProps={{ maxLength: 2 }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            fullWidth
                            color="primary"
                            loading={isLoadingSkipProjectLine}
                            disabled={projectLineDetails.hasStartedWork || projectLineDetails.isWorkOver || isLoadingSkipProjectLine}
                            onClick={() => handleUpdateLine("skip")}
                        >
                            Skip
                        </LoadingButton>
                    </Grid>

                    <Grid item xs={6}>
                        <OpenPhotosButton projectLineId={projectLineId} />
                    </Grid>
                    <Grid item xs={6}>
                        <ReportIncidentButton projectLineId={projectLineId} />
                    </Grid>

                    <Grid item xs={12} style={{ height: 50 + "%" }}>
                        <SamsynMap
                            locationId={projectLine?.project?.address?.id}
                            latitude={projectLine?.project?.address?.latitude}
                            longitude={projectLine?.project?.address?.longitude}
                            serviceType={projectLine?.project?.serviceType?.section}
                            layerName={projectLine?.project?.serviceType?.layerName}
                        />
                    </Grid>

                    {relatedProjectLines && relatedProjectLines.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="h6">Related Project Lines</Typography>
                            <List dense={true} style={{ maxHeight: "25vh", overflow: "auto" }}>
                                {relatedProjectLines.map((line) => (
                                    <ListItem style={{ padding: "0", borderLeft: line?.checkIn === true ? "6px solid #28a745" : "6px solid #b0b0b0" }} key={line.id} button>
                                        <ListItemText
                                            primary={line?.product?.name ?? "Óskráð"}
                                            primaryTypographyProps={{ style: { fontSize: "13px" } }}
                                            secondary={`Magn: ${line?.quantity ?? ""}`}
                                            secondaryTypographyProps={{ style: { fontSize: "11px" } }}
                                            style={{ margin: "5px" }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    )}
                </Grid>
            </Layout>
            <Dialog open={workEntryOpen && isAdminView} onClose={() => !isCreatingWorkline && setWorkEntryOpen(false)} fullWidth maxWidth="sm">
                <Box component="form" onSubmit={(event) => {
                    event.preventDefault();
                    if (validHours && !isCreatingWorkline && isAdminView && user?.id) {
                        createWorkline({ projectLineId, employeeId: user.id, hours: parsedHours, description: workDescription });
                    }
                }}>
                    <DialogTitle>Skrá vinnu</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ mb: 2 }}>Starfsmaður: {user?.name}</Typography>
                        <DurationInput
                            autoFocus required fullWidth
                            value={hours} onChange={setHours}
                            disabled={isCreatingWorkline}
                            sx={{ mb: 2 }}
                        />
                        {validHours && (
                            <Typography sx={{ mb: 2 }}>
                                {previewEnd.subtract(parsedHours * 60, "minute").format("DD.MM.YYYY HH:mm")} – {previewEnd.format("DD.MM.YYYY HH:mm")}
                            </Typography>
                        )}
                        <TextField
                            fullWidth multiline minRows={3} label="Lýsing á vinnu" value={workDescription}
                            onChange={(event) => setWorkDescription(event.target.value)} disabled={isCreatingWorkline}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button disabled={isCreatingWorkline} onClick={() => setWorkEntryOpen(false)}>Hætta við</Button>
                        <LoadingButton type="submit" variant="contained" loading={isCreatingWorkline} disabled={!validHours || isCreatingWorkline || !user?.id}>
                            Skrá vinnulínu
                        </LoadingButton>
                    </DialogActions>
                </Box>
            </Dialog>
            <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)} TransitionComponent={Transition}>
                <DialogContent>
                    <AppBar sx={{ position: "relative" }} style={{ backgroundColor: "rgb(225 190 16)" }}>
                        <Toolbar>
                            <IconButton onClick={() => setFullscreen(false)} aria-label="close" edge="start" color="inherit">
                                <Close />
                            </IconButton>
                            <Typography>{projectLine?.project?.address?.name ?? projectLine?.name ?? ""}</Typography>
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ my: 8, mx: 0 }}>
                        <TextField
                            label={fullscreenLabelAndDescription.label}
                            multiline
                            value={fullscreenLabelAndDescription.description}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ readOnly: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFullscreen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
